/**
 * homing-missile — 追蹤飛彈。動詞:**射後不理、專心躲**。
 *
 * 玩法:慢射(間隔長)。每發飛彈自動轉向追**最近敵人**,但轉向速率有限
 * (turnRate),所以 K-3 能閃過自己人的方向、飛彈不會瞬間鎖死貼臉。射出去之後
 * 玩家專心走位躲敵彈,飛彈自己去收人頭。
 *
 * 設計:飛彈是「武器自己擁有的小實體」(位置+速度+壽命),behavior 每幀:
 *   1. 到間隔 → 生一發(初速朝右,前方)。
 *   2. 每發以 steerToward(限速)朝最近活敵轉向、推進位置。
 *   3. 命中(距任一活敵 < hitRadius)→ 對該敵 takeDamage、飛彈消失。
 *   4. 出畫面 / 壽命到 → 消失。
 * scene adapter 用 missiles() 拿位置畫 sprite;純運動/命中邏輯用 types.ts 純函式。
 * 鐵則:威力 = baseBulletDamage × 倍率 × 等級曲線,不讀修煉值。
 */

import type { WeaponBehavior, WeaponTickContext, Vec2 } from './types';
import {
  steerToward,
  nearestTarget,
  weaponLevelDamageMult,
  applyRateMod,
  sizeRangeMult,
  blastRangeBonus,
  aoeHitIndices,
  pierceDamageFalloff,
  PIERCE_DAMAGE_FALLOFF,
} from './types';

export const HOMING_MISSILE_ID = 'homing-missile';
export const HOMING_MISSILE_BLURB = '慢慢射出會自動追最近敵人的飛彈 — 射完就專心躲,讓飛彈去打。';

/** 發射間隔(ms):刻意慢(放生型),每發較重。 */
export const HOMING_FIRE_INTERVAL_MS = 900;
/** 飛彈速度(px/sec)。 */
export const HOMING_MISSILE_SPEED = 360;
/** 每幀最大轉向(弧度):有限 → 可被閃、不瞬鎖。約 3.4°/幀@60fps 的數倍仍溫和。 */
export const HOMING_MAX_TURN_RAD = 0.07;
/** 命中半徑(px):飛彈中心距敵中心 < 此值即命中。 */
export const HOMING_HIT_RADIUS = 22;
/** 飛彈壽命(sec):追太久沒中就自滅,避免繞圈卡場。 */
export const HOMING_LIFETIME_SEC = 4;
/**
 * 傷害倍率:慢射的單發應比連發機砲重。
 * boss 2026-06-14 第二輪「算範圍×傷害」:homing 是 range★5(全場自動追蹤,射後不理 =
 * 廣)。2.5→2.3 微降,讓其單體 DPS 收進帶內(range★5 不該又廣又高);自動追蹤的免瞄準
 * 便利已是它的價值,單發不需頂標。
 */
export const HOMING_DAMAGE_MULT = 2.3;
/** 一次齊射多發飛彈時,額外飛彈相對主彈的上下發射偏移(px,做成扇形齊射)。 */
export const HOMING_VOLLEY_SPREAD_Y = 18;
/**
 * 命中爆炸的預設視覺半徑(px)。純美術:當這發飛彈不是「爆裂/巨彈」詮釋
 * (blastRadius=0)時,命中點仍畫一圈小爆讓人看得到「打中了」。
 * 不影響任何傷害判定(傷害只看 blastRadius 的 AoE 邏輯,見 tick)。
 */
export const HOMING_IMPACT_FX_RADIUS = 28;

/** 一發在飛的飛彈(scene adapter 讀來畫 sprite)。 */
export interface HomingMissile {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 已存活秒數。 */
  ageSec: number;
  damage: number;
  /**
   * 剩餘穿透/彈跳次數(三選一「穿透/彈跳」詮釋)。命中後若 >0 → 不消失,改追下一隻
   * 並 -1(穿透敵群)。0 = 命中即消失(預設,行為同今日)。
   */
  pierceLeft: number;
  /**
   * 已穿透敵人數(boss 2026-06-14「不能打得又廣傷害又高」)。每穿過一隻 +1,下一隻
   * 吃 damage × pierceDamageFalloff^pierceHits → 穿越後段傷害遞減,「廣」不滿傷。
   * 0 = 尚未穿透,首擊滿傷。
   */
  pierceHits: number;
  /**
   * 命中小爆半徑(px,三選一「爆裂/巨彈」詮釋)。>0 → 命中點對半徑內全活敵施一次
   * AoE(小範圍連坐)。0 = 純單體(預設)。
   */
  blastRadius: number;
}

/**
 * 一次命中事件(scene adapter 每幀 drain 來生爆炸 FX)。
 * 純美術緩衝:behavior 在「本來就會發生的命中」那一刻記一筆,scene 消費後清空。
 * 不參與任何傷害/命中/壽命邏輯。
 */
export interface MissileImpact {
  /** 命中點 x(飛彈當下位置)。 */
  x: number;
  /** 命中點 y。 */
  y: number;
  /**
   * 爆炸視覺半徑(px)。爆裂/巨彈詮釋(blastRadius>0)時 = 該 AoE 半徑(視覺對齊
   * 實際連坐範圍);否則 = HOMING_IMPACT_FX_RADIUS(純單體的小爆)。
   */
  radius: number;
}

export class HomingMissileBehavior implements WeaponBehavior {
  readonly id = HOMING_MISSILE_ID;
  private _missiles: HomingMissile[] = [];
  private _lastFireMs = -1;
  /** 本幀累積的命中事件(scene drainImpacts() 消費後清空)。純美術,不影響行為。 */
  private _impacts: MissileImpact[] = [];

  tick(ctx: WeaponTickContext): void {
    // 三選一詮釋 — homing 版:
    //   攻擊增速 → 發射間隔縮短(interval × effectiveAutoFireMult)。
    //   多顆子彈 → 一次齊射多發飛彈(扇形 ±vy 偏移),全自動追蹤。
    //   穿透/彈跳 → 飛彈命中後不消失,改追下一隻(pierceLeft 次)。
    //   爆裂/巨彈 → 飛彈命中時小範圍爆(blastRadius = spreadBlastRadius × size)。
    const mods = ctx.mods;
    const targets = ctx.enemies();

    // 1. 到間隔生一「齊射」(主彈 + effectiveSpreadBullets 發額外飛彈)。
    const intervalMs = applyRateMod(HOMING_FIRE_INTERVAL_MS, mods.effectiveAutoFireMult());
    if (this._lastFireMs < 0 || ctx.nowMs - this._lastFireMs >= intervalMs) {
      this._lastFireMs = ctx.nowMs;
      const dmg = Math.round(
        ctx.baseBulletDamage() * HOMING_DAMAGE_MULT * weaponLevelDamageMult(ctx.weaponLevel),
      );
      // 穿透/彈跳 → 飛彈帶 pierceLeft(命中後續追);爆裂/巨彈 → 帶小爆半徑。
      const pierceLeft = Math.max(0, Math.floor(mods.pierceCount) + Math.floor(mods.bounceCount + mods.evoBounceBonus));
      const blastRadius = blastRangeBonus(mods.spreadBlastRadius) * sizeRangeMult(mods.bulletSizeMult);
      const extraMissiles = mods.effectiveSpreadBullets();
      const total = 1 + extraMissiles;
      for (let i = 0; i < total; i++) {
        // 扇形齊射:主彈水平,額外彈交錯上下偏移 vy(K-3 看得出「一次射好幾發」)。
        const vy = i === 0 ? 0 : (i % 2 === 1 ? -1 : 1) * Math.ceil(i / 2) * 0.18 * HOMING_MISSILE_SPEED;
        this._missiles.push({
          x: ctx.playerX + 30,
          y: ctx.playerY + (i === 0 ? 0 : (i % 2 === 1 ? -1 : 1) * Math.ceil(i / 2) * HOMING_VOLLEY_SPREAD_Y),
          vx: HOMING_MISSILE_SPEED,
          vy,
          ageSec: 0,
          damage: dmg,
          pierceLeft,
          pierceHits: 0,
          blastRadius,
        });
      }
    }

    // 2~4. 推進每發飛彈:轉向 → 移動 → 命中(穿透/小爆)/壽命判定。
    const survivors: HomingMissile[] = [];
    for (const m of this._missiles) {
      m.ageSec += ctx.dt;
      if (m.ageSec >= HOMING_LIFETIME_SEC) continue; // 壽命到,自滅

      const pos: Vec2 = { x: m.x, y: m.y };
      const tgt = nearestTarget(pos, targets);
      if (tgt) {
        const v = steerToward(
          pos,
          { x: m.vx, y: m.vy },
          { x: tgt.x, y: tgt.y },
          HOMING_MISSILE_SPEED,
          HOMING_MAX_TURN_RAD,
        );
        m.vx = v.x;
        m.vy = v.y;
      }
      m.x += m.vx * ctx.dt;
      m.y += m.vy * ctx.dt;

      // 命中最近活敵(距離 < hitRadius)→ 施傷(+小爆)。
      let hit = false;
      for (const t of targets) {
        if (!t.alive) continue;
        if (Math.hypot(t.x - m.x, t.y - m.y) < HOMING_HIT_RADIUS) {
          // 穿透傷害衰減(boss 2026-06-14):第 N 個被穿目標 = damage × falloff^N。
          // pierceHits=0(未穿透)= 滿傷,所以非穿透飛彈行為同今日。
          const dealt = pierceDamageFalloff(m.damage, m.pierceHits, PIERCE_DAMAGE_FALLOFF);
          t.takeDamage(dealt);
          // 爆裂詮釋:命中點小範圍連坐(圈內其它活敵也吃同(衰減後)傷,不含已打的那隻)。
          if (m.blastRadius > 0) {
            const aoe = aoeHitIndices(targets, m.x, m.y, m.blastRadius);
            for (const idx of aoe) {
              const other = targets[idx];
              if (other && other !== t && other.alive) other.takeDamage(dealt);
            }
          }
          // 純美術:記一筆命中事件給 scene 畫爆炸。爆裂彈用其 AoE 半徑(視覺對齊),
          // 否則用預設小爆半徑。**不影響任何傷害/命中判定**。
          this._impacts.push({
            x: m.x,
            y: m.y,
            radius: m.blastRadius > 0 ? m.blastRadius : HOMING_IMPACT_FX_RADIUS,
          });
          hit = true;
          break;
        }
      }
      if (hit) {
        // 穿透/彈跳詮釋:還有 pierceLeft → 不消失,繼續追下一隻(-1)。
        // pierceHits++ 讓下一隻吃更低傷(falloff),「廣」不滿傷。
        if (m.pierceLeft > 0) {
          m.pierceLeft -= 1;
          m.pierceHits += 1;
          survivors.push(m);
        }
        continue;
      }

      // 出畫面剔除(右/上/下;左不會,初速朝右)。寬鬆界,實際邊界 scene 用畫面尺寸。
      if (m.x > 2000 || m.x < -64 || m.y < -64 || m.y > 2000) continue;

      survivors.push(m);
    }
    this._missiles = survivors;
  }

  reset(): void {
    this._missiles = [];
    this._lastFireMs = -1;
    this._impacts = [];
  }

  /** scene adapter / 測試讀來畫 sprite 或斷言。回唯讀快照。 */
  missiles(): readonly HomingMissile[] {
    return this._missiles;
  }

  /**
   * 取出本幀累積的命中事件並清空緩衝(scene 每幀消費一次生爆炸 FX)。
   * 純美術 seam:回傳後緩衝歸零,所以同一筆命中只會生一次爆炸。
   * 不影響任何行為/傷害。
   */
  drainImpacts(): MissileImpact[] {
    if (this._impacts.length === 0) return [];
    const out = this._impacts;
    this._impacts = [];
    return out;
  }
}
